import React, { Fragment, useState, useEffect } from 'react'
import { Row, Col, Form, Input, Checkbox, Typography, Skeleton, Divider, Button, Modal, message, Dropdown, Menu } from 'antd'
import { PlusOutlined, EllipsisOutlined, CheckOutlined } from '@ant-design/icons'
import { connect, useDispatch } from 'react-redux'
import { postData } from '../../../Actions/Action'
import SaveButton from '../../SaveButton'

const { Title } = Typography

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`

const DEFAULT_PROVIDER_FIELD = { label: 'Shipping Provider', placeholder: 'Please select your Shipping Provider', required: true }
const DEFAULT_SERVICE_FIELD = { label: 'Service', placeholder: 'Please select your Service', required: true }
const DEFAULT_ACCOUNT_FIELD = { label: 'Account Number', placeholder: 'Please enter your Account Number.', required: true }
const EMPTY_DRAFT = { name: '', max_length: '', regex: '', invalid_message: '', postal_code: '' }

const POPULAR_PATTERNS = [
	['Letters, numbers, and spaces', '^[a-zA-Z0-9 ]*$'],
	['Letters and numbers', '^[a-zA-Z0-9]*$'],
	['Letters and spaces', '^[a-zA-Z ]*$'],
	['Letters only', '^[a-zA-Z]*$'],
	['Numbers and spaces', '^[0-9 ]*$'],
	['Numbers only', '^[0-9]*$'],
]

const normalizeProviders = (providers) => {
	if (!Array.isArray(providers)) return []
	return providers.map(p => ({
		...p,
		id: p.id || genId(),
		services: (p.services || []).map(s => ({ ...s, id: s.id || genId() })),
	}))
}

function QuoteSettingsComponent(props) {
	const dispatch = useDispatch()
	const [loading, setLoading] = useState(true)
	const [checkoutLabel, setCheckoutLabel] = useState('Bill My Account')
	const [providerField, setProviderField] = useState(DEFAULT_PROVIDER_FIELD)
	const [serviceField, setServiceField] = useState(DEFAULT_SERVICE_FIELD)
	const [accountField, setAccountField] = useState(DEFAULT_ACCOUNT_FIELD)
	const [providers, setProviders] = useState([])
	const [serviceErrors, setServiceErrors] = useState({})
	const [editingServiceIds, setEditingServiceIds] = useState({})

	const [modalOpen, setModalOpen] = useState(false)
	const [editingProviderId, setEditingProviderId] = useState(null)
	const [draft, setDraft] = useState(EMPTY_DRAFT)

	useEffect(() => {
		if (props.quoteSettings !== null && props.quoteSettings !== undefined) {
			setCheckoutLabel(props.quoteSettings.checkout_label || 'Bill My Account')
			setProviderField({ ...DEFAULT_PROVIDER_FIELD, ...(props.quoteSettings.shipping_provider_field || {}) })
			setServiceField({ ...DEFAULT_SERVICE_FIELD, ...(props.quoteSettings.service_field || {}) })
			setAccountField({ ...DEFAULT_ACCOUNT_FIELD, ...(props.quoteSettings.account_number_field || {}) })
			setProviders(normalizeProviders(props.quoteSettings.providers))
			setLoading(false)
		}
		// eslint-disable-next-line
	}, [props.quoteSettings])

	// ── Modal (Add / Edit a bill-to option) ─────────────────────────────────────

	const openAddModal = () => {
		setEditingProviderId(null)
		setDraft(EMPTY_DRAFT)
		setModalOpen(true)
	}

	const openEditModal = (provider) => {
		setEditingProviderId(provider.id)
		setDraft({
			name: provider.name || '',
			max_length: provider.max_length || '',
			regex: provider.regex || '',
			invalid_message: provider.invalid_message || '',
			postal_code: provider.postal_code || '',
		})
		setModalOpen(true)
	}

	const closeModal = () => {
		setModalOpen(false)
		setDraft(EMPTY_DRAFT)
		setEditingProviderId(null)
	}

	const confirmModal = () => {
		if (!draft.name.trim()) {
			message.error('Shipping Provider is required.')
			return
		}
		if (editingProviderId) {
			setProviders(prev => prev.map(p => (p.id === editingProviderId ? { ...p, ...draft, name: draft.name.trim() } : p)))
		} else {
			setProviders(prev => [
				...prev,
				{
					id: genId(),
					name: draft.name.trim(),
					max_length: draft.max_length,
					regex: draft.regex,
					invalid_message: draft.invalid_message,
					postal_code: draft.postal_code,
					services: [],
				},
			])
		}
		closeModal()
	}

	const removeProvider = (providerId) => {
		setProviders(prev => prev.filter(p => p.id !== providerId))
	}

	// ── Service actions ───────────────────────────────────────────────────────

	const addService = (providerId) => {
		const newId = genId()
		setProviders(prev =>
			prev.map(p =>
				p.id === providerId
					? { ...p, services: [...p.services, { id: newId, name: '' }] }
					: p
			)
		)
		setEditingServiceIds(prev => ({ ...prev, [newId]: true }))
	}

	const removeService = (providerId, serviceId) => {
		setProviders(prev =>
			prev.map(p =>
				p.id === providerId
					? { ...p, services: p.services.filter(s => s.id !== serviceId) }
					: p
			)
		)
	}

	const updateServiceName = (providerId, serviceId, value) => {
		setProviders(prev =>
			prev.map(p =>
				p.id === providerId
					? { ...p, services: p.services.map(s => (s.id === serviceId ? { ...s, name: value } : s)) }
					: p
			)
		)
		if (serviceErrors[serviceId]) {
			setServiceErrors(prev => { const n = { ...prev }; delete n[serviceId]; return n })
		}
	}

	const confirmEditService = (serviceId) => {
		setEditingServiceIds(prev => { const n = { ...prev }; delete n[serviceId]; return n })
	}

	const serviceMenu = (providerId, serviceId) => (
		<Menu>
			<Menu.Item key='edit' onClick={() => setEditingServiceIds(prev => ({ ...prev, [serviceId]: true }))}>
				Edit
			</Menu.Item>
			<Menu.Item key='delete' danger onClick={() => removeService(providerId, serviceId)}>
				Delete
			</Menu.Item>
		</Menu>
	)

	const providerMenu = (provider) => (
		<Menu>
			<Menu.Item key='edit' onClick={() => openEditModal(provider)}>
				Edit
			</Menu.Item>
			<Menu.Item key='delete' danger onClick={() => removeProvider(provider.id)}>
				Delete
			</Menu.Item>
		</Menu>
	)

	// ── Submit ────────────────────────────────────────────────────────────────

	const onFinish = () => {
		if (!checkoutLabel.trim()) {
			message.error('Shipping Method Name is required.')
			return
		}
		const fieldConfigs = [
			['Shipping Provider (Dropdown Options)', providerField],
			['Service (Dropdown Options)', serviceField],
			['Account Number (Text Field)', accountField],
		]
		for (const [title, field] of fieldConfigs) {
			if (!field.label || !field.label.trim()) {
				message.error(`${title} Label is required.`)
				return
			}
			if (!field.placeholder || !field.placeholder.trim()) {
				message.error(`${title} Placeholder Text is required.`)
				return
			}
		}
		const errors = {}
		for (const provider of providers) {
			for (const service of provider.services) {
				if (!service.name.trim()) {
					errors[service.id] = true
				}
			}
		}
		if (Object.keys(errors).length > 0) {
			setServiceErrors(errors)
			message.error('Service name is required. Please fill in all service name fields before saving.')
			return
		}

		const data = {
			carrierId: +props.carrierId,
			checkout_label: checkoutLabel,
			shipping_provider_field: providerField,
			service_field: serviceField,
			account_number_field: accountField,
			providers: providers.map(({ id, ...p }) => ({
				...p,
				services: p.services.map(({ id: _sid, ...s }) => s),
			})),
		}
		dispatch(postData(data, 'GET_QUOTE_SETTINGS', 'submit_quote_settings', props.token))
	}

	// ── Render ────────────────────────────────────────────────────────────────

	if (loading || props.quoteSettings === null || props.quoteSettings === undefined) {
		return <Skeleton active />
	}

	return (
		<Fragment>
			<Form
				layout='vertical'
				name='sac_quote_settings'
				className='form-wrp'
				size='large'
				onFinish={onFinish}>

				{/* Shipping Method Name */}
				<Row gutter={30} className='mb-4'>
					<Col xs={24} sm={24} md={24} lg={6} xl={6} style={{ paddingTop: '11px' }}>
						<label className='text-gray'>Shipping Method Name <span style={{ color: '#ff4d4f' }}>*</span></label>
					</Col>
					<Col xs={24} sm={24} md={24} lg={18} xl={18}>
						<Form.Item
							className='mb-1'
							validateStatus={!checkoutLabel.trim() ? 'error' : ''}
							help={!checkoutLabel.trim() ? 'This field is required.' : null}>
							<Input
								value={checkoutLabel}
								onChange={e => setCheckoutLabel(e.target.value)}
								placeholder='Bill My Account'
							/>
						</Form.Item>
						<div className='text-gray'>
							Displayed to customers as a shipping option during checkout.
						</div>
					</Col>
				</Row>

				{/* Shipping Provider field config */}
				<FieldConfigRow
					title='Shipping Provider (Dropdown Options)'
					field={providerField}
					onChange={setProviderField}
				/>

				{/* Service field config */}
				<FieldConfigRow
					title='Service (Dropdown Options)'
					field={serviceField}
					onChange={setServiceField}
				/>

				{/* Account Number field config */}
				<FieldConfigRow
					title='Account Number (Text Field)'
					field={accountField}
					onChange={setAccountField}
				/>

				{/* Bill-to options header + Add link */}
				<Row gutter={30} className='mb-2' align='middle'>
					<Col flex='auto'>
						<Title level={4} style={{ margin: 0 }}>Bill-to options</Title>
					</Col>
					<Col>
						<a
							href='#add-bill-to-option'
							onClick={e => { e.preventDefault(); openAddModal() }}
							style={{ color: '#1890ff', fontSize: 14 }}>
							<PlusOutlined /> Add a bill-to option
						</a>
					</Col>
				</Row>

				{/* Info note */}
				<Row gutter={30} className='mb-3'>
					<Col xs={24}>
						<div style={{ color: '#8c7b4a', fontSize: 14 }}>
							These options will be displayed as a shipping option on the checkout page.
						</div>
					</Col>
				</Row>

				{/* Provider sections */}
				{providers.map(provider => (
					<Fragment key={provider.id}>
						<Divider />

						{/* Provider header */}
						<Row gutter={16} className='mb-3' align='middle'>
							<Col flex='auto'>
								<Title level={4} style={{ margin: 0 }}>{provider.name}</Title>
							</Col>
							<Col>
								<Dropdown overlay={providerMenu(provider)} trigger={['click']} placement='bottomRight'>
									<Button type='text' icon={<EllipsisOutlined style={{ fontSize: 20 }} />} />
								</Dropdown>
							</Col>
						</Row>

						{/* Service rows */}
						{provider.services.map(service => {
							const isEditing = !!editingServiceIds[service.id]
							return (
								<div
									key={service.id}
									style={{
										display: 'flex',
										alignItems: 'center',
										padding: '10px 14px',
										marginBottom: 4,
										border: '1px solid #d9d9d9',
										borderRadius: 4,
										background: serviceErrors[service.id] ? '#fff1f0' : '#fff',
									}}>
									<div style={{ flex: 1 }}>
										{isEditing ? (
											<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
												<Input
													value={service.name}
													onChange={e => updateServiceName(provider.id, service.id, e.target.value)}
													onPressEnter={() => confirmEditService(service.id)}
													placeholder='Service name *'
													status={serviceErrors[service.id] ? 'error' : ''}
													autoFocus
												/>
												<Button
													type='primary'
													shape='circle'
													icon={<CheckOutlined />}
													onClick={() => confirmEditService(service.id)}
												/>
											</div>
										) : (
											<span style={{
												fontWeight: 600,
												color: serviceErrors[service.id] ? '#ff4d4f' : 'inherit',
											}}>
												{service.name || <span style={{ color: '#bfbfbf', fontWeight: 400 }}>Service name *</span>}
											</span>
										)}
									</div>
									{!isEditing && (
										<Dropdown overlay={serviceMenu(provider.id, service.id)} trigger={['click']} placement='bottomRight'>
											<Button type='text' icon={<EllipsisOutlined style={{ fontSize: 18 }} />} />
										</Dropdown>
									)}
								</div>
							)
						})}

						{/* Add service link */}
						<Row justify='end' className='mt-2 mb-2'>
							<Col>
								<a
									href='#add-service'
									onClick={e => { e.preventDefault(); addService(provider.id) }}
									style={{ color: '#1890ff', fontSize: 14 }}>
									+ Add a service
								</a>
							</Col>
						</Row>
					</Fragment>
				))}

				<SaveButton />
			</Form>

			{/* Add / Edit bill-to option modal */}
			<Modal
				title='Bill To Options - Shipping Account Capture'
				open={modalOpen}
				onCancel={closeModal}
				footer={[
					<Button key='cancel' onClick={closeModal}>Cancel</Button>,
					<Button key='add' type='primary' onClick={confirmModal}>{editingProviderId ? 'Save' : 'Add'}</Button>,
				]}
				width={900}>

				<div style={{ marginBottom: 16 }}>
					<label className='text-gray'>Shipping Provider</label>
					<Input
						value={draft.name}
						onChange={e => setDraft(prev => ({ ...prev, name: e.target.value }))}
						placeholder='e.g., UPS'
						autoFocus
						style={{ marginTop: 4 }}
					/>
				</div>

				<div style={{ border: '1px solid #f0f0f0', borderRadius: 4, padding: 16 }}>
					<div style={{ fontWeight: 600, marginBottom: 12 }}>Account Number Validation</div>
					<Row gutter={12}>
						<Col span={8}>
							<label className='text-gray'>Max Length</label>
							<Input
								value={draft.max_length}
								onChange={e => setDraft(prev => ({ ...prev, max_length: e.target.value.replace(/[^0-9]/g, '') }))}
								placeholder='10'
								style={{ marginTop: 4 }}
							/>
							<div className='text-gray' style={{ fontSize: 12 }}>maximum character length</div>
						</Col>
						<Col span={8}>
							<label className='text-gray'>Regular Expression</label>
							<Input
								value={draft.regex}
								onChange={e => setDraft(prev => ({ ...prev, regex: e.target.value }))}
								placeholder='^[0-9]*$'
								style={{ marginTop: 4 }}
							/>
						</Col>
						<Col span={8}>
							<label className='text-gray'>Regular Expression Invalid Message</label>
							<Input
								value={draft.invalid_message}
								onChange={e => setDraft(prev => ({ ...prev, invalid_message: e.target.value }))}
								placeholder='Please enter a valid account number.'
								style={{ marginTop: 4 }}
							/>
							<div className='text-gray' style={{ fontSize: 12 }}>text to show when regex is not met</div>
						</Col>
					</Row>

					<div style={{ padding: '8px 12px', marginTop: 12 }}>
						<div>
							<Checkbox
								checked={draft.postal_code === 'optional'}
								onChange={e => setDraft(prev => ({ ...prev, postal_code: e.target.checked ? 'optional' : '' }))}>
								Prompt for account associated postal code and make it optional
							</Checkbox>
						</div>
						<div>
							<Checkbox
								checked={draft.postal_code === 'required'}
								onChange={e => setDraft(prev => ({ ...prev, postal_code: e.target.checked ? 'required' : '' }))}>
								Prompt for account associated postal code and make it required
							</Checkbox>
						</div>
					</div>

					<div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: 12, marginTop: 12, fontSize: 13 }}>
						<div style={{ marginBottom: 4 }}>Some popular patterns:</div>
						{POPULAR_PATTERNS.map(([label, pattern]) => (
							<div key={pattern}>
								<i>{label}</i>&nbsp;&nbsp;<code>{pattern}</code>
							</div>
						))}
					</div>
				</div>
			</Modal>
		</Fragment>
	)
}

function FieldConfigRow({ title, field, onChange }) {
	const labelEmpty = !field.label || !field.label.trim()
	const placeholderEmpty = !field.placeholder || !field.placeholder.trim()

	return (
		<Row gutter={30} className='mb-4'>
			<Col xs={24} sm={24} md={24} lg={6} xl={6} style={{ paddingTop: '11px' }}>
				<label className='text-gray'>{title} <span style={{ color: '#ff4d4f' }}>*</span></label>
			</Col>
			<Col xs={24} sm={24} md={24} lg={18} xl={18}>
				<Form.Item
					className='mb-1'
					validateStatus={labelEmpty ? 'error' : ''}
					help={labelEmpty ? 'This field is required.' : null}>
					<Input
						value={field.label}
						onChange={e => onChange({ ...field, label: e.target.value })}
					/>
				</Form.Item>
				<div className='text-gray mb-2'>
					Enter the {title} Label in this field.
				</div>
				<Form.Item
					className='mb-1'
					validateStatus={placeholderEmpty ? 'error' : ''}
					help={placeholderEmpty ? 'This field is required.' : null}>
					<Input
						value={field.placeholder}
						onChange={e => onChange({ ...field, placeholder: e.target.value })}
					/>
				</Form.Item>
				<div className='text-gray mb-2'>
					Enter the {title} Placeholder Text in this field.
				</div>
				<Checkbox
					checked={!!field.required}
					onChange={e => onChange({ ...field, required: e.target.checked })}>
					Required
				</Checkbox>
			</Col>
		</Row>
	)
}

const mapStateToProps = state => ({
	quoteSettings: state.quoteSettings,
	token: state.token,
	carrierId: state.carrierId,
})

export default connect(mapStateToProps)(QuoteSettingsComponent)
